/**
 * web-map.tsx
 *
 * A Leaflet-based map component used as the non-ATAK fallback.
 * Listens for DOM events dispatched by mapBridge (web implementation):
 *   biostream:map-add-marker   → { uid, name, lat, lon, remarks, triageLevel }
 *   biostream:map-focus-marker → { uid }
 *   biostream:map-clear-markers → {}
 *
 * Replays any markers that were buffered before this component mounted.
 */

import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import type { Map as LeafletMap, Marker } from "leaflet";
import type { MarkerInfo } from "./map-bridge";
import { getBufferedMarkers } from "./map-bridge";

const TRIAGE_COLOR: Record<string, string> = {
  T1: "#c0392b",
  T2: "#e67e22",
  T3: "#27ae60",
  T4: "#8e44ad",
  T0: "#95a5a6",
  ASSIGNED: "#f39c12",
};

function markerColor(triageLevel: string): string {
  return TRIAGE_COLOR[triageLevel] ?? "#2980b9";
}

interface WebMapProps {
  height?: string;
}

export function WebMap({ height = "300px" }: WebMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let map: LeafletMap | null = null;
    const markerMap = new Map<string, Marker>();
    let hasFirstMarker = false;
    let destroyed = false;

    function placeMarker(L: typeof import("leaflet"), info: MarkerInfo) {
      if (!map) return;
      const existing = markerMap.get(info.uid);
      if (existing) existing.remove();

      const color = markerColor(info.triageLevel);
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:14px;height:14px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });

      const marker = L.marker([info.lat, info.lon], { icon })
        .addTo(map)
        .bindPopup(`<strong>${info.name}</strong><br/>${info.remarks}`);

      markerMap.set(info.uid, marker);

      if (!hasFirstMarker) {
        hasFirstMarker = true;
        map.setView([info.lat, info.lon], 15);
      }
    }

    (async () => {
      const L = (await import("leaflet")).default;
      if (destroyed || !containerRef.current) return;

      map = L.map(containerRef.current, { zoomControl: true });
      map.setView([0, 0], 2);

      L.tileLayer("/api/tiles/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      setTimeout(() => map?.invalidateSize(), 0);

      // Replay any markers that were added before this component mounted
      for (const info of getBufferedMarkers()) {
        placeMarker(L, info);
      }

      // If we placed markers, fit the map to them
      if (hasFirstMarker && markerMap.size > 1) {
        const bounds = L.latLngBounds(
          Array.from(markerMap.values()).map((m) => m.getLatLng()),
        );
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }

      // Live event listeners
      const handleAdd = async (e: Event) => {
        placeMarker(L, (e as CustomEvent<MarkerInfo>).detail);
      };

      const handleFocus = (e: Event) => {
        const { uid } = (e as CustomEvent<{ uid: string }>).detail;
        const marker = markerMap.get(uid);
        if (!map || !marker) return;
        map.setView(marker.getLatLng(), Math.max(map.getZoom(), 16));
        marker.openPopup();
      };

      const handleClear = () => {
        markerMap.forEach((m) => m.remove());
        markerMap.clear();
        hasFirstMarker = false;
      };

      window.addEventListener("biostream:map-add-marker", handleAdd);
      window.addEventListener("biostream:map-focus-marker", handleFocus);
      window.addEventListener("biostream:map-clear-markers", handleClear);

      // Store cleanup refs on the closure
      (map as any)._cleanupListeners = () => {
        window.removeEventListener("biostream:map-add-marker", handleAdd);
        window.removeEventListener("biostream:map-focus-marker", handleFocus);
        window.removeEventListener("biostream:map-clear-markers", handleClear);
      };
    })();

    return () => {
      destroyed = true;
      (map as any)?._cleanupListeners?.();
      map?.remove();
      map = null;
      markerMap.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width: "100%",
        borderTop: "1px solid var(--border, #333)",
      }}
    />
  );
}
